import * as polygonClipping from "polygon-clipping";
import * as THREE from "three";
import { WallType, JointFillPolygon } from "../types/floorplannerTypes";
import { JointInfo } from "./JointDetector";
import { getWallDirectionAt } from "./edgeConnectionHelpers";
import { getQuadraticBezierPoint, getQuadraticBezierTangent } from "./arcUtils";
import { getMultiJointEndpointTrimPairForJoint } from "./jointEndpointTrimGeometry";
import type { FloorplannerStore } from "../store/floorplannerStore";

/**
 * Joint Geometry - Boolean CSG core for wall junctions
 *
 * PURPOSE:
 * Compute wall-junction geometry as 2D constructive solid geometry (CSG) on the
 * participating wall footprints, instead of guessing a polygon from a cloud of
 * points (the legacy convex-hull / centroid-sort approach).
 *
 * MENTAL MODEL (satisfies all three correctness rules simultaneously):
 *   footprints          = raw rectangle/arc footprint of each participating wall
 *   renderedFootprints  = committed wall-fill footprints after endpoint trimming
 *   cap                 = coverage >= 2 of endpoint-extended footprints
 *   jointFill           = difference(cap, renderedFootprints + nearby blockers)
 *   S                   = union(raw footprints, jointFill)
 *   outline             = boundary(S) // one closed loop around the junction
 *
 * WHY THIS IS CORRECT:
 * - Rule 1 (fill only gaps, never overlap a wall): jointFill is cap minus the
 *   committed wall-fill footprints, so it is disjoint from rendered wall
 *   interiors -> no 3D z-fighting.
 * - Rule 2 (closed outline, no leaks): the outline is the boundary of the union of
 *   everything solid; a flood fill cannot escape a union boundary.
 * - Rule 3 (order independence): union/difference are commutative & associative and
 *   depend only on geometry (positions, widths, angles), never on join/render order.
 *
 * ANGLE HANDLING:
 * The boolean ops are angle-agnostic. The only angle-sensitive step is constructing
 * the miter wedge between two walls: it is bounded by the two walls' facing outer
 * edges and closed at their intersection (the miter point), which is exact at any
 * angle. Near-collinear walls (miter at infinity) get a flat bridge instead.
 *
 * @see jointPolygonGenerator.ts - consumes this for fill + outline
 * @see JOINT_POLYGON_RENDERING.md
 */

type Pt = [number, number];
type Ring = Pt[];
type Polygon = Ring[];
type MultiPolygon = Polygon[];
type Bounds = { minX: number; minY: number; maxX: number; maxY: number };

const EPSILON = 1e-7;
const MIN_RING_AREA = 1e-9;
const ARC_FOOTPRINT_SEGMENTS = 24;
// Quantize coordinates to improve robustness of the boolean operations against
// near-coincident edges produced by offset wall rectangles.
const QUANTIZE = 1e6;

// Lazy accessor to avoid a circular dependency with floorplannerStore.
let _store: FloorplannerStore | null = null;
const getStore = (): FloorplannerStore => {
  if (!_store) {
    const globalStore = (globalThis as typeof globalThis & {
      __boniFloorplannerStore?: FloorplannerStore;
    }).__boniFloorplannerStore;
    if (!globalStore) {
      throw new Error("FloorplannerStore not yet initialized - jointGeometry called too early");
    }
    _store = globalStore;
  }
  return _store;
};

const defaultWidth = (): number => getStore().wallWidth;

const vec = (p: THREE.Vector2 | { x: number; y: number }): THREE.Vector2 =>
  new THREE.Vector2(p.x, p.y);

const quantize = (value: number): number => Math.round(value * QUANTIZE) / QUANTIZE;

const toPt = (p: THREE.Vector2 | { x: number; y: number }): Pt => [quantize(p.x), quantize(p.y)];

const ptToVector2 = (p: Pt): THREE.Vector2 => new THREE.Vector2(p[0], p[1]);

const ringArea = (ring: Ring): number => {
  let area = 0;
  for (let i = 0; i < ring.length; i++) {
    const next = (i + 1) % ring.length;
    area += ring[i][0] * ring[next][1] - ring[next][0] * ring[i][1];
  }
  return area / 2;
};

const ringBounds = (ring: Ring): Bounds | null => {
  if (ring.length === 0) {
    return null;
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of ring) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return { minX, minY, maxX, maxY };
};

const polygonBounds = (polygon: Polygon): Bounds | null => polygon[0] ? ringBounds(polygon[0]) : null;

const multiPolygonBounds = (multiPolygon: MultiPolygon): Bounds | null => {
  let bounds: Bounds | null = null;
  for (const polygon of multiPolygon) {
    const next = polygonBounds(polygon);
    if (!next) {
      continue;
    }
    bounds = bounds
      ? {
        minX: Math.min(bounds.minX, next.minX),
        minY: Math.min(bounds.minY, next.minY),
        maxX: Math.max(bounds.maxX, next.maxX),
        maxY: Math.max(bounds.maxY, next.maxY),
      }
      : next;
  }
  return bounds;
};

const boundsOverlap = (a: Bounds, b: Bounds, tolerance = EPSILON): boolean =>
  a.minX <= b.maxX + tolerance &&
  a.maxX + tolerance >= b.minX &&
  a.minY <= b.maxY + tolerance &&
  a.maxY + tolerance >= b.minY;

const wallRoughBounds = (wall: WallType): Bounds => {
  const width = wall.wallWidth ?? defaultWidth();
  const pad = width / 2 + EPSILON;
  const points = [vec(wall.start), vec(wall.end)];
  if (wall.controlPoint) {
    points.push(vec(wall.controlPoint));
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }
  return { minX: minX - pad, minY: minY - pad, maxX: maxX + pad, maxY: maxY + pad };
};

/**
 * Clean a ring: drop non-finite and consecutive-duplicate points, ensure it is
 * closed (first === last), and reject degenerate rings (< 3 unique points or
 * near-zero area). Returns a closed ring suitable for polygon-clipping.
 */
const cleanRing = (points: Array<THREE.Vector2 | Pt | { x: number; y: number }>): Ring | null => {
  const open: Ring = [];
  for (const point of points) {
    const pt: Pt = Array.isArray(point) ? [quantize(point[0]), quantize(point[1])] : toPt(point);
    if (!Number.isFinite(pt[0]) || !Number.isFinite(pt[1])) {
      continue;
    }
    const last = open[open.length - 1];
    if (!last || Math.hypot(last[0] - pt[0], last[1] - pt[1]) > EPSILON) {
      open.push(pt);
    }
  }

  if (open.length > 1) {
    const first = open[0];
    const last = open[open.length - 1];
    if (Math.hypot(first[0] - last[0], first[1] - last[1]) <= EPSILON) {
      open.pop();
    }
  }

  if (open.length < 3 || Math.abs(ringArea(open)) < MIN_RING_AREA) {
    return null;
  }

  return [...open, [open[0][0], open[0][1]]];
};

// ---------------------------------------------------------------------------
// Wall footprints
// ---------------------------------------------------------------------------

/**
 * Exact 2D footprint polygon of a wall (straight rectangle or tessellated arc band).
 * This is the geometry the junction boolean operates on.
 */
export function wallFootprint(wall: WallType): Polygon | null {
  const width = wall.wallWidth ?? defaultWidth();

  if (wall.lineForm === "arc" && wall.controlPoint) {
    return arcFootprint(wall, width);
  }

  const start = vec(wall.start);
  const end = vec(wall.end);
  const direction = new THREE.Vector2().subVectors(end, start);
  if (direction.lengthSq() < 1e-12) {
    return null;
  }
  direction.normalize();
  const perp = new THREE.Vector2(-direction.y, direction.x).multiplyScalar(width / 2);

  const ring = cleanRing([
    new THREE.Vector2().addVectors(start, perp),
    new THREE.Vector2().addVectors(end, perp),
    new THREE.Vector2().subVectors(end, perp),
    new THREE.Vector2().subVectors(start, perp),
  ]);

  return ring ? [ring] : null;
}

const arcFootprint = (wall: WallType, width: number): Polygon | null => {
  const p0 = vec(wall.start);
  const p1 = vec(wall.controlPoint as THREE.Vector2);
  const p2 = vec(wall.end);
  const half = width / 2;

  const left: THREE.Vector2[] = [];
  const right: THREE.Vector2[] = [];

  for (let i = 0; i <= ARC_FOOTPRINT_SEGMENTS; i++) {
    const t = i / ARC_FOOTPRINT_SEGMENTS;
    const point = getQuadraticBezierPoint(t, p0, p1, p2);
    const tangent = getQuadraticBezierTangent(t, p0, p1, p2);
    const perp =
      tangent.lengthSq() > 1e-12
        ? new THREE.Vector2(-tangent.y, tangent.x).normalize()
        : new THREE.Vector2(0, 1);
    left.push(point.clone().addScaledVector(perp, half));
    right.push(point.clone().addScaledVector(perp, -half));
  }

  // Left edge start->end, then right edge end->start to close the band.
  const ring = cleanRing([...left, ...right.reverse()]);
  return ring ? [ring] : null;
};

// ---------------------------------------------------------------------------
// Extended footprints (gap bridging)
// ---------------------------------------------------------------------------

/**
 * Footprint of a wall with its joint-side endpoint extended outward by `cap`
 * along the wall direction. Used to bridge the gap at the joint: where two
 * extended footprints OVERLAP, there is genuine joint material to fill; where a
 * single extension pokes out alone, it is an artificial overshoot to discard.
 *
 * Through-wall (middle) connections already span the joint, so they are returned
 * unextended.
 */
const extendedWallFootprint = (
  wall: WallType,
  connectionPosition: "start" | "end" | "middle",
  cap: number,
): MultiPolygon => {
  const base = wallFootprint(wall);
  if (cap <= 0 || connectionPosition === "middle" || !base) {
    return base ? [base] : [];
  }

  const width = wall.wallWidth ?? defaultWidth();

  if (wall.lineForm === "arc" && wall.controlPoint) {
    // Arcs: keep the curved footprint and append a straight stub along the
    // endpoint tangent, then union the two.
    const stub = endpointStub(wall, connectionPosition, width, cap);
    if (!stub) {
      return [base];
    }
    try {
      return polygonClipping.union(base, [stub]) as MultiPolygon;
    } catch {
      return [base];
    }
  }

  // Straight wall: move the connected endpoint outward along the wall direction.
  const start = vec(wall.start);
  const end = vec(wall.end);
  const direction = new THREE.Vector2().subVectors(end, start);
  if (direction.lengthSq() < 1e-12) {
    return [base];
  }
  direction.normalize();

  const extStart = connectionPosition === "start" ? start.clone().addScaledVector(direction, -cap) : start;
  const extEnd = connectionPosition === "end" ? end.clone().addScaledVector(direction, cap) : end;
  const perp = new THREE.Vector2(-direction.y, direction.x).multiplyScalar(width / 2);

  const ring = cleanRing([
    new THREE.Vector2().addVectors(extStart, perp),
    new THREE.Vector2().addVectors(extEnd, perp),
    new THREE.Vector2().subVectors(extEnd, perp),
    new THREE.Vector2().subVectors(extStart, perp),
  ]);
  return ring ? [[ring]] : [base];
};

/** Straight rectangular stub extending outward from a wall endpoint (arc walls). */
const endpointStub = (
  wall: WallType,
  endpoint: "start" | "end",
  width: number,
  cap: number,
): Ring | null => {
  const base = endpoint === "start" ? vec(wall.start) : vec(wall.end);
  const into = getWallDirectionAt(wall, endpoint);
  if (into.lengthSq() < 1e-12) {
    return null;
  }
  into.normalize();
  // getWallDirectionAt points into the wall body at "start" and out of it at "end".
  const outward = endpoint === "start" ? into.clone().negate() : into.clone();
  const far = base.clone().addScaledVector(outward, cap);
  const perp = new THREE.Vector2(-outward.y, outward.x).multiplyScalar(width / 2);
  return cleanRing([
    new THREE.Vector2().addVectors(base, perp),
    new THREE.Vector2().addVectors(far, perp),
    new THREE.Vector2().subVectors(far, perp),
    new THREE.Vector2().subVectors(base, perp),
  ]);
};

// ---------------------------------------------------------------------------
// Boolean assembly
// ---------------------------------------------------------------------------

export interface JointSolidResult {
  /** Combined solid: union(wall footprints, gap fill). */
  solid: MultiPolygon;
  /** Combined solid used for visible outline extraction. Kept for compatibility. */
  visualSolid: MultiPolygon;
  /** Union of the raw wall footprints (no extensions). */
  wallsUnion: MultiPolygon;
  /**
   * Local 2D joint cap: region covered by at least two endpoint-extended wall
   * footprints. Unlike fill, this intentionally includes overlap with raw wall
   * bodies, so it is diagnostic geometry only and must not be rendered as
   * visible fill.
   */
  cover: MultiPolygon;
  /** Gap-only fill (covered by >=2 extended footprints, minus visible wall fills). */
  fill: MultiPolygon;
  /** Footprints keyed by wall id, used for outline classification. */
  footprints: Array<{ wallId: string; polygon: Polygon }>;
  /** Committed wall fill footprints after multi-joint endpoint trimming. */
  renderedFootprints: Array<{ wallId: string; polygon: Polygon }>;
}

const safeUnion = (polygons: Polygon[]): MultiPolygon => {
  const valid = polygons.filter(p => Array.isArray(p) && p.length > 0);
  if (valid.length === 0) {
    return [];
  }
  if (valid.length === 1) {
    return [valid[0]];
  }
  return polygonClipping.union(valid[0], ...valid.slice(1)) as MultiPolygon;
};

const collectFillBlockerFootprints = (
  walls: Map<string, WallType>,
  fillCandidate: MultiPolygon,
  excludeWallIds: Set<string> = new Set(),
): Polygon[] => {
  const candidateBounds = multiPolygonBounds(fillCandidate);
  if (!candidateBounds) {
    return [];
  }

  const blockers: Polygon[] = [];
  for (const wall of walls.values()) {
    if (excludeWallIds.has(wall.id)) {
      continue;
    }
    if (!boundsOverlap(candidateBounds, wallRoughBounds(wall))) {
      continue;
    }
    const footprint = wallFootprint(wall);
    if (!footprint) {
      continue;
    }
    const footprintBounds = polygonBounds(footprint);
    if (footprintBounds && boundsOverlap(candidateBounds, footprintBounds)) {
      blockers.push(footprint);
    }
  }
  return blockers;
};

const renderedWallFootprintForJointConnection = (
  joint: JointInfo,
  connection: JointInfo["connectedWalls"][number],
  wall: WallType,
): Polygon | null => {
  const raw = wallFootprint(wall);
  if (!raw || connection.connectionPosition === "middle" || wall.lineForm === "arc") {
    return raw;
  }

  const trimPair = getMultiJointEndpointTrimPairForJoint(joint, wall, connection.connectionPosition, defaultWidth);
  if (!trimPair) {
    return raw;
  }

  const start = vec(wall.start);
  const end = vec(wall.end);
  const direction = new THREE.Vector2().subVectors(end, start);
  if (direction.lengthSq() < 1e-12) {
    return raw;
  }

  direction.normalize();
  const width = wall.wallWidth ?? defaultWidth();
  const perp = new THREE.Vector2(-direction.y, direction.x).multiplyScalar(width / 2);
  let startLeft = new THREE.Vector2().addVectors(start, perp);
  let endLeft = new THREE.Vector2().addVectors(end, perp);
  let endRight = new THREE.Vector2().subVectors(end, perp);
  let startRight = new THREE.Vector2().subVectors(start, perp);

  if (connection.connectionPosition === "start") {
    startLeft = trimPair.left;
    startRight = trimPair.right;
  } else {
    endLeft = trimPair.left;
    endRight = trimPair.right;
  }

  const ring = cleanRing([startLeft, endLeft, endRight, startRight]);
  return ring ? [ring] : raw;
};

const localJointWindow = (center: THREE.Vector2 | { x: number; y: number }, radius: number): Polygon | null => {
  const c = vec(center);
  const ring = cleanRing([
    new THREE.Vector2(c.x - radius, c.y - radius),
    new THREE.Vector2(c.x + radius, c.y - radius),
    new THREE.Vector2(c.x + radius, c.y + radius),
    new THREE.Vector2(c.x - radius, c.y + radius),
  ]);
  return ring ? [ring] : null;
};

/**
 * Build the boolean solid for a joint. Deterministic and order-independent.
 *
 * The gap fill is the region covered by AT LEAST TWO extended wall footprints,
 * minus the visible committed wall fills. This bridges corner gaps, miters,
 * T-caps and width-mismatch "dominant" caps uniformly, while discarding
 * single-extension overshoot (which only one wall reaches into).
 *
 * Returns null if there is no usable geometry (fewer than 2 footprints).
 */
export function buildJointSolid(joint: JointInfo, walls: Map<string, WallType>): JointSolidResult | null {
  const footprints: Array<{ wallId: string; polygon: Polygon }> = [];
  const renderedFootprints: Array<{ wallId: string; polygon: Polygon }> = [];
  const connections: Array<{ wallId: string; position: "start" | "end" | "middle" }> = [];
  for (const connection of joint.connectedWalls) {
    const wall = walls.get(connection.wallId);
    if (!wall) {
      continue;
    }
    const polygon = wallFootprint(wall);
    if (polygon) {
      footprints.push({ wallId: connection.wallId, polygon });
      renderedFootprints.push({
        wallId: connection.wallId,
        polygon: renderedWallFootprintForJointConnection(joint, connection, wall) ?? polygon,
      });
      connections.push({ wallId: connection.wallId, position: connection.connectionPosition });
    }
  }

  if (footprints.length < 2) {
    return null;
  }

  const maxWidth = footprints.reduce((max, f) => {
    const width = walls.get(f.wallId)?.wallWidth ?? defaultWidth();
    return Math.max(max, width);
  }, defaultWidth());
  // Extend each wall past the joint by ~one wall width. This is just enough for
  // two converging walls' extensions to meet across the gap, but NOT so far that
  // a wall's extension crosses BEYOND a non-adjacent wall's edge and creates a
  // phantom overlap there. (Extending to exactly a neighbor's edge yields a
  // zero-area overlap, so no spurious fill is produced behind it.)
  const cap = maxWidth;

  try {
    const footprintPolygons = footprints.map(f => f.polygon);
    const wallsUnion = safeUnion(footprintPolygons);

    const extended = connections.map(c => {
      const wall = walls.get(c.wallId)!;
      return extendedWallFootprint(wall, c.position, cap);
    });

    // Coverage >= 2 = union over all pairs of (extended_i INTERSECT extended_j).
    let coverage2: MultiPolygon = [];
    for (let i = 0; i < extended.length; i++) {
      for (let j = i + 1; j < extended.length; j++) {
        if (extended[i].length === 0 || extended[j].length === 0) {
          continue;
        }
        const overlap = polygonClipping.intersection(extended[i], extended[j]) as MultiPolygon;
        if (Array.isArray(overlap) && overlap.length > 0) {
          coverage2 = coverage2.length > 0
            ? (polygonClipping.union(coverage2, overlap) as MultiPolygon)
            : overlap;
        }
      }
    }

    let fill = coverage2;
    const participatingWallIds = new Set(footprints.map(footprint => footprint.wallId));
    const renderedFootprintPolygons = renderedFootprints.map(footprint => footprint.polygon);
    const fillBlockerFootprints = coverage2.length > 0
      ? [
        ...renderedFootprintPolygons,
        ...collectFillBlockerFootprints(walls, coverage2, participatingWallIds),
      ]
      : [];
    const fillBlockers = fillBlockerFootprints.length > 0 ? fillBlockerFootprints : footprintPolygons;
    const fillBlockersUnion = safeUnion(fillBlockers);
    if (coverage2.length > 0 && fillBlockersUnion.length > 0) {
      try {
        fill = polygonClipping.difference(coverage2, fillBlockersUnion) as MultiPolygon;
      } catch {
        // Some coincident-edge obtuse joints can make polygon-clipping fail when
        // subtracting a pre-unioned wall body. Subtracting each blocker footprint
        // in sequence is equivalent for our disjoint wall solids and is more stable.
        fill = coverage2;
        try {
          for (const footprint of fillBlockers) {
            if (fill.length === 0) {
              break;
            }
            fill = polygonClipping.difference(fill, footprint) as MultiPolygon;
          }
        } catch {
          // Last-resort safety: an empty fill is preferable to corrupt geometry
          // or wall-overlapping fill. The raw wall union still renders normally.
          fill = [];
        }
      }
    }

    const solid = fill.length > 0
      ? (polygonClipping.union(wallsUnion, fill) as MultiPolygon)
      : wallsUnion;
    let cover = coverage2;
    if (coverage2.length > 0) {
      const window = localJointWindow(joint.point, Math.max(maxWidth / 2, 0.001));
      if (window) {
        try {
          const localCover = polygonClipping.intersection(coverage2, window) as MultiPolygon;
          cover = fill.length > 0
            ? (polygonClipping.union(localCover, fill) as MultiPolygon)
            : localCover;
        } catch {
          cover = fill.length > 0 ? fill : coverage2;
        }
      }
    }
    const visualSolid = solid;

    return { solid, visualSolid, wallsUnion, cover, fill, footprints, renderedFootprints };
  } catch (error) {
    console.warn(`[jointGeometry] Boolean operation failed for joint ${joint.id}`, error);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Output conversion
// ---------------------------------------------------------------------------

export type FillPolygon = JointFillPolygon;

const ringToVector2 = (ring: Ring): THREE.Vector2[] => {
  const open = ring.length > 1 && Math.hypot(ring[0][0] - ring[ring.length - 1][0], ring[0][1] - ring[ring.length - 1][1]) <= EPSILON
    ? ring.slice(0, -1)
    : ring;
  return open.map(ptToVector2);
};

/** Convert a polygon-clipping MultiPolygon into renderable fill polygons (with holes). */
export function multiPolygonToFillPolygons(multiPolygon: MultiPolygon): FillPolygon[] {
  const result: FillPolygon[] = [];
  for (const polygon of multiPolygon) {
    if (!polygon || polygon.length === 0) {
      continue;
    }
    const outer = ringToVector2(polygon[0]);
    if (outer.length < 3) {
      continue;
    }
    const holes: THREE.Vector2[][] = [];
    for (let i = 1; i < polygon.length; i++) {
      const hole = ringToVector2(polygon[i]);
      if (hole.length >= 3) {
        holes.push(hole);
      }
    }
    result.push({ outer, holes });
  }
  return result;
}

// ---------------------------------------------------------------------------
// Closing outline extraction (boundary of S that walls do not draw themselves)
// ---------------------------------------------------------------------------

export interface JointOutlineEdge {
  start: THREE.Vector2;
  end: THREE.Vector2;
  /** Nearest participating wall, used for color/width/dashed styling. */
  wallId?: string;
}

const distancePointToSegment = (point: THREE.Vector2, a: THREE.Vector2, b: THREE.Vector2): number => {
  const ab = new THREE.Vector2().subVectors(b, a);
  const lenSq = ab.lengthSq();
  if (lenSq < 1e-12) {
    return point.distanceTo(a);
  }
  const t = Math.max(0, Math.min(1, point.clone().sub(a).dot(ab) / lenSq));
  return point.distanceTo(a.clone().addScaledVector(ab, t));
};

const pointOnSegment = (
  point: THREE.Vector2,
  a: THREE.Vector2,
  b: THREE.Vector2,
  tolerance = 1e-6,
): boolean => distancePointToSegment(point, a, b) <= tolerance;

const pointInRingStrict = (point: THREE.Vector2, ring: Ring): boolean => {
  const open = ring.length > 1 && Math.hypot(ring[0][0] - ring[ring.length - 1][0], ring[0][1] - ring[ring.length - 1][1]) <= EPSILON
    ? ring.slice(0, -1)
    : ring;

  for (let i = 0; i < open.length; i++) {
    const a = ptToVector2(open[i]);
    const b = ptToVector2(open[(i + 1) % open.length]);
    if (pointOnSegment(point, a, b)) {
      return false;
    }
  }

  let inside = false;
  for (let i = 0, j = open.length - 1; i < open.length; j = i++) {
    const a = open[i];
    const b = open[j];
    if (
      a[1] > point.y !== b[1] > point.y &&
      point.x < ((b[0] - a[0]) * (point.y - a[1])) / (b[1] - a[1]) + a[0]
    ) {
      inside = !inside;
    }
  }
  return inside;
};

const pointInPolygonStrict = (point: THREE.Vector2, polygon: Polygon): boolean => {
  if (!polygon[0] || !pointInRingStrict(point, polygon[0])) {
    return false;
  }
  for (let i = 1; i < polygon.length; i++) {
    if (pointInRingStrict(point, polygon[i])) {
      return false;
    }
  }
  return true;
};

/**
 * The footprint edge segments that should be treated as already owned by the
 * wall footprint. For multi-joints this includes exact endpoint caps even when
 * the wall outline suppresses them; the joint overlay should draw miter/step
 * bridges, not redraw a flat wall-end cap that closes an otherwise open joint.
 */
const wallOwnedSegments = (
  connection: JointInfo["connectedWalls"][number],
  wall: WallType,
  includeJointEndpointCap: boolean,
): Array<[THREE.Vector2, THREE.Vector2]> => {
  const footprint = wallFootprint(wall);
  if (!footprint) {
    return [];
  }
  const ring = footprint[0];
  const open = ring.length > 1 && Math.hypot(ring[0][0] - ring[ring.length - 1][0], ring[0][1] - ring[ring.length - 1][1]) <= EPSILON
    ? ring.slice(0, -1)
    : ring;

  const width = wall.wallWidth ?? defaultWidth();
  const jointBase = connection.connectionPosition === "start"
    ? vec(wall.start)
    : connection.connectionPosition === "end"
      ? vec(wall.end)
      : null;

  const wallDir = new THREE.Vector2().subVectors(vec(wall.end), vec(wall.start));
  const hasDir = wallDir.lengthSq() > 1e-12;
  if (hasDir) {
    wallDir.normalize();
  }

  const segments: Array<[THREE.Vector2, THREE.Vector2]> = [];
  for (let i = 0; i < open.length; i++) {
    const a = ptToVector2(open[i]);
    const b = ptToVector2(open[(i + 1) % open.length]);
    const mid = a.clone().add(b).multiplyScalar(0.5);
    const segDir = b.clone().sub(a);
    const segLen = segDir.length();
    if (segLen < EPSILON) {
      continue;
    }
    segDir.divideScalar(segLen);

    if (!includeJointEndpointCap && jointBase && hasDir) {
      const perpendicular = Math.abs(segDir.dot(wallDir)) < 0.2;
      if (perpendicular && mid.distanceTo(jointBase) <= width * 0.75) {
        continue;
      }
    }

    segments.push([a, b]);
  }
  return segments;
};

const nearestWallId = (
  mid: THREE.Vector2,
  joint: JointInfo,
  walls: Map<string, WallType>,
): string | undefined => {
  let best: { wallId: string; dist: number } | undefined;
  for (const connection of joint.connectedWalls) {
    const wall = walls.get(connection.wallId);
    if (!wall) {
      continue;
    }
    const dist = distancePointToSegment(mid, vec(wall.start), vec(wall.end));
    if (!best || dist < best.dist) {
      best = { wallId: connection.wallId, dist };
    }
  }
  return best?.wallId;
};

/**
 * Extract the joint's closing outline edges from the boundary of the real joined
 * solid: raw wall footprints plus gap-only fill. The overlap cover is
 * intentionally ignored here; otherwise the joint can redraw wall-owned edges or
 * create a visible outline around fill that should not exist.
 * These are the boundary segments NOT drawn by the walls themselves: miter
 * bridges, width-mismatch step caps, and flat caps between near-collinear walls.
 */
export function extractClosingOutline(
  result: JointSolidResult,
  joint: JointInfo,
  walls: Map<string, WallType>,
): JointOutlineEdge[] {
  const isTwoWallEndpointCorner =
    joint.connectedWalls.length === 2 &&
    joint.connectedWalls.every(connection =>
      connection.connectionPosition === "start" || connection.connectionPosition === "end"
    );
  if (isTwoWallEndpointCorner) {
    return [];
  }

  const tolerance = 0.01;
  const edges: JointOutlineEdge[] = [];

  // Precompute the footprint segments treated as wall-owned for outline
  // subtraction. Multi-joints also own exact endpoint caps so the overlay does
  // not redraw a flat wall-end closure.
  const ownedSegments: Array<[THREE.Vector2, THREE.Vector2]> = [];
  const includeJointEndpointCaps = joint.connectedWalls.length >= 3;
  for (const connection of joint.connectedWalls) {
    const wall = walls.get(connection.wallId);
    if (wall) {
      ownedSegments.push(...wallOwnedSegments(connection, wall, includeJointEndpointCaps));
    }
  }

  // For a boundary edge, return the 1D intervals [t0,t1] (in edge param space,
  // 0..length) that are covered by a collinear wall-owned segment. polygon-clipping
  // merges contiguous collinear wall+cap edges into one segment, so we must
  // subtract the wall-covered portion rather than classify the whole edge.
  const coveredIntervals = (
    start: THREE.Vector2,
    edgeDir: THREE.Vector2,
    length: number,
  ): Array<[number, number]> => {
    const intervals: Array<[number, number]> = [];
    for (const [a, b] of ownedSegments) {
      const segDir = b.clone().sub(a);
      const segLen = segDir.length();
      if (segLen < EPSILON) {
        continue;
      }
      segDir.divideScalar(segLen);
      // Must be (anti)parallel to the edge to be the same line.
      if (Math.abs(edgeDir.x * segDir.y - edgeDir.y * segDir.x) > 0.02) {
        continue;
      }
      // Both endpoints must lie on the edge's infinite line.
      const perpA = Math.abs(a.clone().sub(start).cross(edgeDir));
      const perpB = Math.abs(b.clone().sub(start).cross(edgeDir));
      if (perpA > tolerance || perpB > tolerance) {
        continue;
      }
      let ta = a.clone().sub(start).dot(edgeDir);
      let tb = b.clone().sub(start).dot(edgeDir);
      if (ta > tb) {
        [ta, tb] = [tb, ta];
      }
      const lo = Math.max(0, ta);
      const hi = Math.min(length, tb);
      if (hi - lo > EPSILON) {
        intervals.push([lo, hi]);
      }
    }
    return intervals;
  };

  for (const polygon of result.solid) {
    for (const ring of polygon) {
      const points = ring.length > 1 && Math.hypot(ring[0][0] - ring[ring.length - 1][0], ring[0][1] - ring[ring.length - 1][1]) <= EPSILON
        ? ring.slice(0, -1)
        : ring;
      for (let i = 0; i < points.length; i++) {
        const start = ptToVector2(points[i]);
        const end = ptToVector2(points[(i + 1) % points.length]);
        const edgeVec = new THREE.Vector2().subVectors(end, start);
        const length = edgeVec.length();
        if (length < EPSILON) {
          continue;
        }
        const edgeDir = edgeVec.clone().divideScalar(length);

        // Subtract wall-covered intervals; emit the remaining sub-segments.
        const covered = coveredIntervals(start, edgeDir, length).sort((p, q) => p[0] - q[0]);
        let cursor = 0;
        const emit = (from: number, to: number) => {
          if (to - from <= tolerance) {
            return;
          }
          const segStart = start.clone().addScaledVector(edgeDir, from);
          const segEnd = start.clone().addScaledVector(edgeDir, to);
          const mid = segStart.clone().add(segEnd).multiplyScalar(0.5);
          if (result.footprints.some(footprint => pointInPolygonStrict(mid, footprint.polygon))) {
            return;
          }
          edges.push({ start: segStart, end: segEnd, wallId: nearestWallId(mid, joint, walls) });
        };
        for (const [lo, hi] of covered) {
          if (lo > cursor) {
            emit(cursor, lo);
          }
          cursor = Math.max(cursor, hi);
        }
        if (cursor < length) {
          emit(cursor, length);
        }
      }
    }
  }

  return edges;
}
